import { useState, useCallback } from 'react';
import { useNotificationContext } from '../../../providers/NotificationProvider';
import { setSessionVar, getSessionVar, handleError } from '../../../lib/helpers';
import { useSourcesData } from './useSourcesData';
import { useSelectionAndActions } from './useSelectionAndActions';
import { useBulkAndDialogs } from './useBulkAndDialogs';
import { useDialogsLogic } from './useDialogsLogic';
import { getSourceWarningInfo } from '../../../components/PreflightMonitor/utils/preflightUtils';

export const useSourcesTable = ({
    combinedSearch,
    selectedRowKeys: externalSelectedRowKeys,
    onSelectedRowKeysChange,
    bulkDeleteTriggerRef,
    bulkConfigureMigrationTriggerRef,
    bulkInspectSourcesTriggerRef,
    addSourceModalOpen,
    onAddSourceModalClose,
    bulkConfigureInSituTriggerRef,
    preflightTriggerRef,
}) => {
    const userOrg = sessionStorage.getItem('userOrg');
    const { notifySuccess, notifyError } = useNotificationContext();

    // Selection state (kept here to preserve API)
    const [selectedRowKeys, setSelectedRowKeys] = useState([]);
    const effectiveSelectedRowKeys = externalSelectedRowKeys !== undefined ? externalSelectedRowKeys : selectedRowKeys;
    const setEffectiveSelectedRowKeys = onSelectedRowKeysChange || setSelectedRowKeys;

    // Drawer state
    const [drawerOpen, setDrawerOpen] = useState(false);
    const [selectedSourceId, setSelectedSourceId] = useState(null);
    const [sourcePreflightData, setSourcePreflightData] = useState(null);

    // Preflight modal state (kept in main hook to preserve API)
    const [preflightModalOpen, setPreflightModalOpen] = useState(false);
    const [preflightRequests, setPreflightRequests] = useState([]);
    const [preflightModalConfig, setPreflightModalConfig] = useState({});

    // Data, pagination, expanded, polling
    const {
        tableData,
        expandedRowKeys,
        setExpandedRowKeys,
        pagination,
        setPagination,
        pollingCount,
        isLoading,
        isFetching,
        error,
        refetch,
    } = useSourcesData({ userOrg, combinedSearch });

    // getSourcesByIds preserved
    const getSourcesByIds = useCallback(
        (sourceIds) => {
            const allSources = [];
            tableData.forEach((item) => {
                if (item.isGroup && item.children) {
                    item.children.forEach((child) => {
                        if (sourceIds.includes(child.id)) allSources.push(child);
                    });
                } else if (!item.isGroup && sourceIds.includes(item.id)) {
                    allSources.push(item);
                }
            });
            return allSources;
        },
        [tableData],
    );

    // Selection, row click, rowSelection
    const {
        onSelectChange,
        handleRowClick: handleRowClickInternal,
        rowSelection,
    } = useSelectionAndActions({
        tableData,
        expandedRowKeys,
        setExpandedRowKeys,
        effectiveSelectedRowKeys,
        setEffectiveSelectedRowKeys,
    });

    const handleRowClick = useCallback(
        (e, record) => {
            handleRowClickInternal(e, record, setSelectedSourceId, setDrawerOpen, setSourcePreflightData);
        },
        [handleRowClickInternal],
    );

    // Bulk and dialogs state/handlers
    const bulkAndDialogs = useBulkAndDialogs({
        notifySuccess,
        notifyError,
        getSourcesByIds,
        bulkDeleteTriggerRef,
        bulkConfigureMigrationTriggerRef,
        bulkInspectSourcesTriggerRef,
        userOrg,
        bulkConfigureInSituTriggerRef,
        preflightTriggerRef,
        setPreflightRequests,
        setPreflightModalConfig,
        setPreflightModalOpen,
    });

    const {
        deleteConfirmOpen,
        recordToDelete,
        bulkDeleteMode,
        sourcesToDelete,
        highlightedForDeleteId,
        isDeleting,
        editModalOpen,
        editSourceId,
        migrationReadinessDialogOpen,
        selectedSourcesForMigration,
        sourceInspectionDialogOpen,
        selectedSourcesForInspection,
        inSituReadinessDialogOpen,
        selectedSourcesForInSitu,
        handleBulkDeleteTrigger,
        handleBulkConfigureMigrationTrigger,
        handleBulkInspectSourcesTrigger,
        handleBulkConfigureInSituTrigger,
        handleDeleteCancel,
        handleDeleteSource,
        handleDeleteSingleSource,
        handleEditSource,
        handleInspectSource,
        handleConfigureInSitu,
        setRecordToDelete,
        setEditModalOpen,
        setEditSourceId,
        setSelectedSourcesForInspection,
        setSourceInspectionDialogOpen,
        setSelectedSourcesForMigration,
        setMigrationReadinessDialogOpen,
        setHighlightedForDeleteId,
        setSelectedSourcesForInSitu,
        setInSituReadinessDialogOpen,
    } = bulkAndDialogs;

    // Dialog logic handlers (confirm flows)
    const {
        handleStartMigration,
        handleMigrationReadinessConfirm,
        handleSourceInspectionDialogConfirm,
        handleInSituConfirm,
    } = useDialogsLogic({
        notifyError,
        notifySuccess,
        userOrg,
        getSourcesByIds,
        handleMigrationReadinessDialogClose: useCallback(() => {
            setMigrationReadinessDialogOpen(false);
            setSelectedSourcesForMigration([]);
        }, [setMigrationReadinessDialogOpen, setSelectedSourcesForMigration]),
        setMigrationReadinessDialogOpen,
        setSelectedSourcesForMigration,
        setPreflightRequests,
        setPreflightModalConfig,
        setPreflightModalOpen,
        selectedSourcesForInspection,
        handleSourceInspectionDialogClose: useCallback(() => {
            setSourceInspectionDialogOpen(false);
            setSelectedSourcesForInspection([]);
        }, [setSourceInspectionDialogOpen, setSelectedSourcesForInspection]),
        selectedSourcesForInSitu,
        handleInSituDialogClose: useCallback(() => {
            setInSituReadinessDialogOpen(false);
            setSelectedSourcesForInSitu([]);
        }, [setInSituReadinessDialogOpen, setSelectedSourcesForInSitu]),
    });

    // Edit modal close/cancel handlers (preserved)
    const handleEditModalClose = useCallback(() => {
        if (addSourceModalOpen) {
            onAddSourceModalClose?.();
        } else {
            setEditModalOpen(false);
            setEditSourceId(null);
        }
        refetch();
    }, [addSourceModalOpen, onAddSourceModalClose, refetch, setEditModalOpen, setEditSourceId]);

    const handleEditModalCancel = useCallback(() => {
        if (addSourceModalOpen) {
            onAddSourceModalClose?.();
        } else {
            setEditModalOpen(false);
            setEditSourceId(null);
        }
    }, [addSourceModalOpen, onAddSourceModalClose, setEditModalOpen, setEditSourceId]);

    // Preflight modal handlers (unchanged)
    const handlePreflightModalClose = useCallback(() => {
        setPreflightModalOpen(false);
        setPreflightRequests([]);
        setPreflightModalConfig({});
    }, []);

    const handlePreflightModalComplete = useCallback(
        (result) => {
            if (preflightModalConfig.type === 'source_inspection') {
                handlePreflightModalClose();
                return;
            }
            try {
                if (preflightModalConfig.type === 'source_inspection' || preflightModalConfig.type === 'migration') {
                    const preflights = result.preflights || [];
                    const ignoreValidationErrors =
                        preflightModalConfig.migrationContext?.ignoreValidationErrors || false;
                    const hasErrorSources = preflights.some((c) => c.state === 'error' && c.errorType !== 'warning');
                    if (hasErrorSources && !ignoreValidationErrors) {
                        notifyError({
                            description:
                                'Cannot proceed due to fatal errors in preflight checks. Please resolve the errors and try again.',
                        });
                        return;
                    }
                    const selectedSources =
                        preflightModalConfig.type === 'source_inspection'
                            ? selectedSourcesForInspection
                            : selectedSourcesForMigration;
                    const validSources =
                        preflights.length > 0
                            ? preflights
                                  .filter((c) => {
                                      const isSuccessOrWarning =
                                          c.state === 'success' || (c.state === 'error' && c.errorType === 'warning');
                                      const isFailedButIgnored =
                                          ignoreValidationErrors && c.state === 'error' && c.errorType !== 'warning';
                                      return (isSuccessOrWarning || isFailedButIgnored) && c.type !== 'target_cloud';
                                  })
                                  .map((c) => c.resource_id)
                            : selectedSources.map((s) => s.id);
                    const warnings =
                        preflights.length > 0
                            ? preflights
                                  .filter((c) => c.state === 'error' && c.errorType === 'warning')
                                  .map((c) => {
                                      const warningInfo = getSourceWarningInfo(c);
                                      return {
                                          source: c.resource_id,
                                          source_host: warningInfo.source_host,
                                          source_hostname: warningInfo.source_hostname,
                                          preflight: c.preflight_id,
                                      };
                                  })
                            : [];
                    const selectedAccount =
                        preflightModalConfig.migrationContext?.targetAccount ||
                        preflightModalConfig.migrationContext?.cloudAccount?.id;
                    if (selectedAccount) {
                        fetch(`/api/v3/cloudaccounts/${selectedAccount}/cachedattributes`, {
                            method: 'GET',
                            credentials: 'include',
                            headers: { accept: 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
                        })
                            .then((response) => {
                                if (response.ok) return response.json();
                                throw new Error(`Failed to fetch cloud attributes: ${response.status}`);
                            })
                            .then((model) => {
                                setSessionVar('acctAttributeModel', { ...model });
                                const migrationOptions = {
                                    sources: validSources,
                                    destinations: [],
                                    target: selectedAccount,
                                    target_type: model.cloud_type || 'aws',
                                    preflight_warnings: warnings,
                                    preflight_recoveryList: [],
                                    data_only_migration:
                                        preflightModalConfig.migrationContext?.dataOnlyMigration || false,
                                    migrate_netapp_files:
                                        preflightModalConfig.migrationContext?.migrate_netapp_files || false,
                                    vdi_migration: preflightModalConfig.migrationContext?.vdi_migration || false,
                                    migration_type: preflightModalConfig.migrationContext?.in_situ
                                        ? 'in_situ_modernization'
                                        : preflightModalConfig.migrationContext?.migrationType || 'full',
                                    block_mobility_target_type:
                                        preflightModalConfig.migrationContext?.block_mobility_target_type ||
                                        'existing_disk_on_host',
                                    in_situ: preflightModalConfig.migrationContext?.in_situ || false,
                                    claudAccountRegion:
                                        preflightModalConfig.migrationContext?.cloudAccount?.appliance?.cloud_properties
                                            ?.region,
                                    claudAccountZone:
                                        preflightModalConfig.migrationContext?.cloudAccount?.appliance?.cloud_properties
                                            ?.az,
                                };
                                setSessionVar('migrationOptions', migrationOptions);
                                if (!getSessionVar('entitlementId') && !getSessionVar('entitlementsAlreadyConsumed')) {
                                    setSessionVar('entitlementsAlreadyConsumed', true);
                                }
                                setTimeout(() => {
                                    if (window.reactNavigate) {
                                        window.reactNavigate('migration_plan');
                                    } else {
                                        window.location.href = '/migration_plan';
                                    }
                                }, 100);
                                const flowType = preflightModalConfig.migrationContext?.in_situ
                                    ? 'In-Situ readiness check'
                                    : preflightModalConfig.type === 'source_inspection'
                                      ? 'Source inspection'
                                      : 'Migration readiness check';
                                notifySuccess({
                                    description: `${flowType} successful. Redirecting to migration planning...`,
                                });
                            })
                            .catch((error) => {
                                console.error('Error fetching cloud attributes:', error);
                                notifyError({
                                    description: 'Error fetching target cloud attributes. Please try again.',
                                });
                            });
                    } else {
                        const migrationOptions = {
                            sources: validSources,
                            destinations: [],
                            target: null,
                            target_type: 'aws',
                            preflight_warnings: warnings,
                            preflight_recoveryList: [],
                            data_only_migration: preflightModalConfig.migrationContext?.dataOnlyMigration || false,
                            migrate_netapp_files: preflightModalConfig.migrationContext?.migrate_netapp_files || false,
                            vdi_migration: preflightModalConfig.migrationContext?.vdi_migration || false,
                            migration_type: preflightModalConfig.migrationContext?.in_situ
                                ? 'in_situ_modernization'
                                : preflightModalConfig.migrationContext?.migrationType || 'full',
                            block_mobility_target_type:
                                preflightModalConfig.migrationContext?.block_mobility_target_type ||
                                'existing_disk_on_host',
                            in_situ: preflightModalConfig.migrationContext?.in_situ || false,
                        };
                        setSessionVar('migrationOptions', migrationOptions);
                        if (!getSessionVar('entitlementId') && !getSessionVar('entitlementsAlreadyConsumed')) {
                            setSessionVar('entitlementsAlreadyConsumed', true);
                        }
                        setTimeout(() => {
                            if (window.reactNavigate) {
                                window.reactNavigate('migration_plan');
                            } else {
                                window.location.href = '/migration_plan';
                            }
                        }, 100);
                        const flowType = preflightModalConfig.migrationContext?.in_situ
                            ? 'In-Situ readiness check'
                            : preflightModalConfig.type === 'source_inspection'
                              ? 'Source inspection'
                              : 'Migration readiness check';
                        notifySuccess({ description: `${flowType} successful. Redirecting to migration planning...` });
                    }
                } else {
                    notifyError({ description: 'Unknown preflight type. Please try again.' });
                }
                handlePreflightModalClose();
                refetch();
            } catch (error) {
                console.error('Error in handlePreflightModalComplete:', error);
                handleError(error, notifyError, 'Failed to complete preflight process');
            }
        },
        [
            preflightModalConfig,
            selectedSourcesForInspection,
            selectedSourcesForMigration,
            handlePreflightModalClose,
            notifySuccess,
            notifyError,
            refetch,
        ],
    );

    return {
        tableData,
        userOrg,
        isLoading,
        isFetching,
        isDeleting,
        error,
        pagination,
        expandedRowKeys,
        effectiveSelectedRowKeys,
        drawerOpen,
        selectedSourceId,
        sourcePreflightData,
        deleteConfirmOpen,
        bulkDeleteMode,
        sourcesToDelete,
        recordToDelete,
        editModalOpen,
        editSourceId,
        highlightedForDeleteId,
        migrationReadinessDialogOpen,
        selectedSourcesForMigration,
        sourceInspectionDialogOpen,
        selectedSourcesForInspection,
        inSituReadinessDialogOpen,
        selectedSourcesForInSitu,
        preflightModalOpen,
        preflightRequests,
        preflightModalConfig,
        pollingCount,
        handleRowClick,
        handlePaginationChange: useCallback((page, pageSize) => {
            setPagination((prev) => ({ ...prev, current: page, pageSize }));
        }, []),
        handleDeleteSource: useCallback(
            (sourceId) => {
                bulkAndDialogs.handleDeleteSource(sourceId, refetch, setEffectiveSelectedRowKeys);
            },
            [bulkAndDialogs, refetch, setEffectiveSelectedRowKeys],
        ),
        handleDeleteCancel,
        onSelectChange,
        refetch,
        handleStartMigration,
        handleInspectSource,
        handleBulkInspectSourcesTrigger,
        handleSourceInspectionDialogClose: useCallback(() => {
            setSourceInspectionDialogOpen(false);
            setSelectedSourcesForInspection([]);
        }, [setSourceInspectionDialogOpen, setSelectedSourcesForInspection]),
        handleSourceInspectionDialogConfirm,
        handleEditSource,
        handleEditModalClose,
        handleEditModalCancel,
        handleConfigureUpgrade: useCallback(
            (sourceId) => {
                handleConfigureInSitu(sourceId);
            },
            [handleConfigureInSitu],
        ),
        handleDeleteSingleSource,
        handleMigrationReadinessDialogClose: useCallback(() => {
            setMigrationReadinessDialogOpen(false);
            setSelectedSourcesForMigration([]);
        }, [setMigrationReadinessDialogOpen, setSelectedSourcesForMigration]),
        handleMigrationReadinessConfirm,
        handlePreflightModalClose,
        handlePreflightModalComplete,
        handleInSituConfirm,
        rowSelection,
        setDrawerOpen,
        setSelectedSourceId,
        setExpandedRowKeys,
        setEditModalOpen,
        setEditModalId: setEditSourceId,
        setHighlightedForDeleteId,
        setInSituReadinessDialogOpen,
    };
};
